// GENERAL UTILS...

import { IErrorResponse, ISuccessResponse } from './ts/interfaces';

import { errorCodeMessages } from './errorCodeMessages';

export const is200xRange = (value: number | string) => {
  return parseInt(value as string) >= 200 && parseInt(value as string) < 300;
};

export const is400xRange = (value: number | string) => {
  return parseInt(value as string) >= 400 && parseInt(value as string) < 500;
};

export const isBadRequestError = (value: number | string) => {
  const numValue = typeof value === 'number' ? value : parseInt(value, 10);
  return numValue === 400;
};

export const onApiSuccess = (
  response: ISuccessResponse,
  callback: object | null = null,
) => {
  if (callback && typeof callback === 'function') {
    callback();
  }

  const responseCode = response?.data?.resultCode;

  const responseMessage = response?.data?.resultMsg?.length
    ? response.data.resultMsg
    : response?.statusText || '';

  const messageWithCode = `${responseCode} - ${responseMessage}`;
  const message = is200xRange(responseCode) ? responseMessage : messageWithCode;

  return {
    response: response.data,
    statusCode: response.status,
    responseCode: response?.data?.resultCode,
    messageWithCode,
    message,
    error: null,
  };
};

export const onApiError = (
  error: IErrorResponse,
  callback: object | null = null,
) => {
  if (callback && typeof callback === 'function') {
    callback();
  }

  const { response } = error || {};
  const responseCode = response?.data?.resultCode;

  let errorMessage = response?.data?.resultMsg
    ? response.data.resultMsg
    : error.message;

  let message;
  if (errorMessage === 'Network Error') {
    message = `${errorMessage}`;
  } else {
    if (typeof responseCode !== 'undefined') {
      errorMessage = errorCodeMessages[responseCode]
        ? errorCodeMessages[responseCode]
        : errorMessage;

      message = `${errorMessage}`;
    } else {
      message = `${errorMessage}`;
    }
  }

  return {
    error,
    statusCode: response?.status,
    responseCode,
    messageWithCode: message,
    message,
    response: null,
    responseData: response?.data,
  };
};

export const displayMessage = (
  endpoint: string,
  method: string,
  code?: string,
) => {
  if (!code) return '';

  // SIGN IN
  if (endpoint === '/admin/jwt/signin') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '관리자 인증이 실패했습니다.';
        case '30010':
          return '관리자 비밀번호가 틀립니다.';
        case '30130':
          return '신청이 반려된 관리자 계정입니다.';
        case '30199':
          return '관리자 상태가 정상이 아닙니다.';
        default:
          return '';
      }
    }
  }

  // AUTH
  if (endpoint === '/admin/jwt/auth') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '관리자 인증 토큰 인가가 실패했습니다.';
        case '15200':
          return '인증 토큰 유효성 검증이 실패했습니다. 인증 절차를 다시 진행하십시오.';
        case '15210':
          return '인증 토큰이 만료되었습니다. 재발급을 진행하십시오.';
        case '15230':
          return 'ROLE 검증이 실패했습니다.';
        default:
          return '';
      }
    }
  }

  // REISSUE
  if (endpoint === '/admin/jwt/reissue') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '관리자 인가 검증이 실패했습니다.';
        case '15300':
          return 'Token 유효성 검증이 실패했습니다. 인증 절차를 다시 진행하십시오.';
        case '15310':
          return 'Token 유효시간이 만료되었습니다.';
        case '15230':
          return 'ROLE 검증이 실패했습니다.';
        default:
          return '';
      }
    }
  }

  // MYPAGE INFO GET
  if (endpoint === '/admin/mypage/info') {
    if (method === 'GET') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '조회가 실패했습니다.';
        case '30010':
          return '로그인한 관리자의 정보가 없습니다.';
        default:
          return '';
      }
    }
  }

  // MYPAGE INFO PUT
  if (endpoint === '/admin/mypage/info') {
    if (method === 'PUT') {
      switch (code) {
        case '1':
          return '수정 되었습니다.';
        case '0':
          return '수정이 실패했습니다.';
        case '12010':
          return '빈값이 허용되지 않는 수정 항목이 있습니다.';
        case '12110':
          return '허용되지 않은 형식의 값이 수정 항목에 있습니다.';
        case '12202':
          return '이메일(도메인) 형식이 아닙니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 수정 항목이 있습니다.';
        default:
          return '';
      }
    }
  }

  // PASSWORD CHECK POST
  if (endpoint === '/admin/password/check') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '현재 비밀번호 검증이 실패했습니다.';
        case '30010':
          return '로그인한 관리자의 정보가 없습니다.';
        case '30500':
          return '현재 비밀번호가 아닙니다. 다시 입력하십시오.';
        case '12010':
          return '로그인한 관리자의 비밀번호를 입력하세요.';
        case '12110':
          return '현재 비밀번호는 9 ~ 20자까지 입력할 수 있습니다.';
        case '12301':
          return '비밀번호 조합 규칙이 틀립니다. (영어 대문자, 영어소문자, 숫자, 특수문자 각 1개 이상 조합)';
        default:
          return '';
      }
    }
  }

  // PASSWORD CHANGE GET
  if (endpoint === '/admin/password/change') {
    if (method === 'GET') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '현재 비밀번호 확인 후 이용하십시오.';
        case '30010':
          return '로그인한 관리자의 정보가 없습니다.';
        default:
          return '';
      }
    }
  }

  // PASSWORD CHANGE PUT
  if (endpoint === '/admin/password/change') {
    if (method === 'PUT') {
      switch (code) {
        case '1':
          return '수정 되었습니다.';
        case '0':
          return '수정이 실패했습니다.';
        case '30010':
          return '로그인한 관리자의 정보가 없습니다.';
        case '12010':
          return '변경할 비밀번호를 입력하세요.';
        case '12110':
          return '변경할 비밀번호는 9 ~ 20자까지 입력할 수 있습니다.';
        case '12301':
          return '변경할 비밀번호 조합 규칙이 틀립니다. (영어 대문자, 영어소문자, 숫자, 특수문자 각 1개 이상 조합)';
        default:
          return '';
      }
    }
  }

  // LFS LIST GET
  if (endpoint === '/admin/lfs/onetime') {
    if (method === 'GET') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '고객 전달용 대용량 파일 목록 조회가 실패했습니다.';
        case '12301':
          return '검색 조건이 허용된 길이 조건에 맞지 않습니다.';
        case '12110':
          return '검색 조건에 특수문자는 사용할 수 없습니다.';
        default:
          return '';
      }
    }
  }

  // LFS INFO GET (DYNAMIC)
  if (endpoint === '/admin/lfs/onetime/{id}') {
    if (method === 'GET') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '선택한 고객 전달용 대용량 파일 정보가 없습니다.';
        case '53010':
          return '만료일자가 지난 정보는 조회할 수 없습니다.';
        case '12010':
          return '조회할 항목을 선택하세요.';
        default:
          return '';
      }
    }
  }

  // LFS INFO POST
  if (endpoint === '/admin/lfs/onetime') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          // return '등록 되었습니다.';
          return '';
        case '0':
          return '등록이 실패했습니다.';
        case '30010':
          return '로그인한 관리자의 정보가 없습니다.';
        case '12010':
          return '빈값이 허용되지 않는 입력 항목이 있습니다.';
        case '12110':
          return '허용되지 않은 형식의 값이 입력 항목에 있습니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 입력 항목이 있습니다.';
        default:
          return '';
      }
    }
  }

  // LFS INFO PUT
  if (endpoint === '/admin/lfs/onetime') {
    if (method === 'PUT') {
      switch (code) {
        case '1':
          return '수정 되었습니다.';
        case '0':
          return '수정이 실패했습니다.';
        case '30010':
          return '로그인한 관리자의 정보가 없습니다.';
        case '53010':
          return '만료일자가 지난 정보는 수정할 수 없습니다.';
        case '12010':
          return '빈값이 허용되지 않는 수정 항목이 있습니다.';
        case '12110':
          return '허용되지 않은 형식의 값이 수정 항목에 있습니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 수정 항목이 있습니다.';
        default:
          return '';
      }
    }
  }

  // LFS INFO DELETE
  if (endpoint === '/admin/lfs/onetime') {
    if (method === 'DELETE') {
      switch (code) {
        case '1':
          return '삭제 되었습니다.';
        case '0':
          return '삭제가 실패했습니다.';
        case '12010':
          return '삭제할 항목을 선택하세요.';
        default:
          return '';
      }
    }
  }

  // LFS S3 UPLOAD ID POST
  if (endpoint === '/admin/lfs/onetime/s3/upload/id') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return 'S3업로드ID 생성이 실패했습니다.';
        case '53110':
          return '더 이상 업로드 할 수 없습니다. ';
        case '53120':
          return '만료일이 지나서 업로드할 수 없습니다.';
        case '12010':
          return '빈값이 허용되지 않는 전송 항목이 있습니다.';
        case '12110':
          return '허용되지 않은 형식의 값이 전송 항목에 있습니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 전송 항목이 있습니다.';
        default:
          return '';
      }
    }
  }

  // LFS S3 PRESIGNED URL POST
  if (endpoint === '/admin/lfs/onetime/s3/part/presigned/url') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return 'Presigned-Url 요청이 실패했습니다.';
        case '12010':
          return '빈값이 허용되지 않는 전송 항목이 있습니다.';
        case '12110':
          return '허용되지 않은 형식의 값이 전송 항목에 있습니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 전송 항목이 있습니다.';
        default:
          return '';
      }
    }
  }

  // LFS S3 PART ETAG INSERT POST
  if (endpoint === '/admin/lfs/onetime/s3/part/etag/insert') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return 'E-Tag 정보 업데이트가 실패했습니다.';
        case '12010':
          return '빈값이 허용되지 않는 전송 항목이 있습니다.';
        case '12110':
          return '허용되지 않은 형식의 값이 전송 항목에 있습니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 전송 항목이 있습니다.';
        default:
          return '';
      }
    }
  }

  // LFS S3 UPLOAD STOP POST
  if (endpoint === '/admin/lfs/onetime/s3/upload/stop') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '업로드가 중지되었습니다.';
        case '0':
          return '업로드 중지가 실패했습니다.';
        case '12010':
          return '빈값이 허용되지 않는 전송 항목이 있습니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 전송 항목이 있습니다.';
        default:
          return '';
      }
    }
  }

  // LFS S3 REPORT DONE POST

  if (endpoint === '/admin/lfs/onetime/s3/report/done') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '등록 되었습니다.';
        case '0':
          return '등록이 실패했습니다.';
        case '12010':
          return '빈값이 허용되지 않는 전송 항목이 있습니다.';
        case '12110':
          return '허용되지 않은 형식의 값이 전송 항목에 있습니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 전송 항목이 있습니다.';
        case '14100':
          return '멀티파트 E-Tag 리스트 값이 정의된 형식이 아닙니다.';
        case '14110':
          return '멀티파트 E-Tag 리스트 값 검증이 실패했습니다.';
        default:
          return '';
      }
    }
  }

  // EXTERNAL DELIVERY

  //  LFS SHARE LIST GET

  if (endpoint === '/admin/lfs/share') {
    if (method === 'GET') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '외부 공유용 대용량 파일 목록 조회가 실패했습니다.';
        case '12301':
          return '검색 조건이 허용된 길이 조건에 맞지 않습니다.';
        case '12110':
          return '검색 조건에 특수문자는 사용할 수 없습니다.';
        default:
          return '';
      }
    }
  }

  // LFS SHARE DETAIL GET (DYNAMIC)
  if (endpoint === '/admin/lfs/share/{id}') {
    if (method === 'GET') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '선택한 고객 전달용 대용량 파일 정보가 없습니다.';
        case '12010':
          return '조회할 항목을 선택하세요.';
        default:
          return '';
      }
    }
  }

  // LFS SHARE INFO POST
  if (endpoint === '/admin/lfs/share') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          // return '등록 되었습니다.';
          return '';
        case '0':
          return '등록이 실패했습니다.';
        case '30010':
          return '로그인한 관리자의 정보가 없습니다.';
        case '12010':
          return '빈값이 허용되지 않는 입력 항목이 있습니다.';
        case '12110':
          return '허용되지 않은 형식의 값이 입력 항목에 있습니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 입력 항목이 있습니다.';
        default:
          return '';
      }
    }
  }

  // LFS SHARE INFO PUT

  if (endpoint === '/admin/lfs/share') {
    if (method === 'PUT') {
      switch (code) {
        case '1':
          return '수정 되었습니다.';
        case '0':
          return '수정이 실패했습니다.';
        case '30010':
          return '로그인한 관리자의 정보가 없습니다.';
        case '12010':
          return '빈값이 허용되지 않는 수정 항목이 있습니다.';
        case '12110':
          return '허용되지 않은 형식의 값이 수정 항목에 있습니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 수정 항목이 있습니다.';
        default:
          return '';
      }
    }
  }

  // LFS SHARE INFO DELETE
  if (endpoint === '/admin/lfs/share') {
    if (method === 'DELETE') {
      switch (code) {
        case '1':
          return '삭제 되었습니다.';
        case '0':
          return '삭제가 실패했습니다.';
        case '12010':
          return '삭제할 항목을 선택하세요.';
        default:
          return '';
      }
    }
  }

  // LFS SHARE S3 UPLOAD ID POST
  if (endpoint === '/api/lfs/share/s3/upload/id') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return 'S3업로드ID 생성이 실패했습니다.';
        case '53110':
          return '더 이상 업로드 할 수 없습니다. ';
        case '12010':
          return '빈값이 허용되지 않는 전송 항목이 있습니다.';
        case '12110':
          return '허용되지 않은 형식의 값이 전송 항목에 있습니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 전송 항목이 있습니다.';
        default:
          return '';
      }
    }
  }

  // LFS SHARE S3 PRESIGNED URL POST
  if (endpoint === '/admin/lfs/share/s3/part/presigned/url') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return 'Presigned-Url 요청이 실패했습니다.';
        case '12010':
          return '빈값이 허용되지 않는 전송 항목이 있습니다.';
        case '12110':
          return '허용되지 않은 형식의 값이 전송 항목에 있습니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 전송 항목이 있습니다.';
        default:
          return '';
      }
    }
  }

  // LFS SHARE S3 PART ETAG INSERT POST

  if (endpoint === '/admin/lfs/share/s3/part/etag/insert') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return 'E-Tag 정보 업데이트가 실패했습니다.';
        case '12010':
          return '빈값이 허용되지 않는 전송 항목이 있습니다.';
        case '12110':
          return '허용되지 않은 형식의 값이 전송 항목에 있습니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 전송 항목이 있습니다.';
        default:
          return '';
      }
    }
  }

  // LFS SHARE S3 UPLOAD STOP POST
  if (endpoint === '/admin/lfs/share/s3/upload/stop') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '업로드가 중지되었습니다.';
        case '0':
          return '업로드 중지가 실패했습니다.';
        case '12010':
          return '빈값이 허용되지 않는 전송 항목이 있습니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 전송 항목이 있습니다.';
        default:
          return '';
      }
    }
  }

  // LFS SHARE S3 REPORT DONE POST

  if (endpoint === '/admin/lfs/share/s3/report/done') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '등록 되었습니다.';
        case '0':
          return '등록이 실패했습니다.';
        case '12010':
          return '빈값이 허용되지 않는 전송 항목이 있습니다.';
        case '12110':
          return '허용되지 않은 형식의 값이 전송 항목에 있습니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 전송 항목이 있습니다.';
        case '14100':
          return '멀티파트 E-Tag 리스트 값이 정의된 형식이 아닙니다.';
        case '14110':
          return '멀티파트 E-Tag 리스트 값 검증이 실패했습니다.';
        default:
          return '';
      }
    }
  }

  // LFS SHARE S3 UPLOAD DELETE
  if (endpoint === '/admin/lfs/share/s3/upload/delete') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '삭제되었습니다.';
        case '0':
          return '삭제가 실패했습니다.';
        case '11010':
          return '이미 삭제된 파일입니다.';
        case '12010':
          return '파일정보PK 항목을 선택하세요.';
        default:
          return '';
      }
    }
  }

  // MANAGEMENT INFO LIST GET

  if (endpoint === '/admin/management/staff') {
    if (method === 'GET') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '직원 목록 조회가 실패했습니다.';
        case '12301':
          return '검색 조건이 허용된 길이 조건에 맞지 않습니다.';
        case '12110':
          return '검색 조건에 특수문자는 사용할 수 없습니다.';
        default:
          return '';
      }
    }
  }

  // MANAGEMENT INFO GET (DYNAMIC)

  if (endpoint === '/admin/management/staff/{id}') {
    if (method === 'GET') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '선택한 직원 조회가 실패했습니다.';
        case '12010':
          return '조회할 항목을 선택하세요.';
        default:
          return '';
      }
    }
  }

  // MANAGEMENT INFO POST

  if (endpoint === '/admin/management/staff') {
    if (method === 'PUT') {
      switch (code) {
        case '1':
          return '수정 되었습니다.';
        case '0':
          return '수정이 실패했습니다.';
        case '30010':
          return '로그인한 관리자의 정보가 없습니다.';
        case '12010':
          return '빈값이 허용되지 않는 입력 항목이 있습니다.';
        default:
          return '';
      }
    }
  }

  // HISTORY DOWNLOAD ONETIME LIST GET

  if (endpoint === '/admin/history/download/onetime') {
    if (method === 'GET') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '고객 전달용 파일 다운로드 이력 목록 조회가 실패했습니다.';

        case '12301':
          return '검색 조건이 허용된 길이 조건에 맞지 않습니다.';

        case '12110':
          return '검색 조건에 특수문자는 사용할 수 없습니다.';
        default:
          return '';
      }
    }
  }

  // HISTORY DOWNLOAD ONETIME INFO GET (DYNAMIC)

  if (endpoint === '/admin/history/download/onetime/{id}') {
    if (method === 'GET') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '선택한 이력 조회가 실패했습니다.';
        case '12010':
          return '조회할 항목을 선택하세요.';
        default:
          return '';
      }
    }
  }

  // HISTORY DOWNLOAD SHARE LIST GET

  if (endpoint === '/admin/history/download/share') {
    if (method === 'GET') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '외부 공유용 파일 다운로드 이력 목록 조회가 실패했습니다.';
        case '12301':
          return '검색 조건이 허용된 길이 조건에 맞지 않습니다.';
        case '12110':
          return '검색 조건에 특수문자는 사용할 수 없습니다.';
        default:
          return '';
      }
    }
  }

  // HISTORY DOWNLOAD SHARE INFO GET (DYNAMIC)

  if (endpoint === '/admin/history/download/share/{id}') {
    if (method === 'GET') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '선택한 이력 조회가 실패했습니다.';
        case '12010':
          return '조회할 항목을 선택하세요.';
        default:
          return '';
      }
    }
  }

  // CONFIG GET

  if (endpoint === '/admin/config/env') {
    if (method === 'GET') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '환경설정 정보 조회가 실패했습니다.';
        case '95':
          return '접근 권한이 없습니다.';
        default:
          return '';
      }
    }
  }

  // CONFIG PUT

  if (endpoint === '/admin/config/env') {
    if (method === 'PUT') {
      switch (code) {
        case '1':
          return '환경설정 데이터 전송되었습니다.';
        case '0':
          return '환경설정 데이터 전송이 실패했습니다.';
        case '12010':
          return '빈값이 허용되지 않는 전송 항목이 있습니다.';
        case '12110':
          return '허용되지 않은 형식의 값이 전송 항목에 있습니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 전송 항목이 있습니다.';
        default:
          return '';
      }
    }
  }

  // MANAGEMENT ACCOUNT LIST GET

  if (endpoint === '/admin/management/account') {
    if (method === 'GET') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '관리자 목록 조회가 실패했습니다.';
        case '12301':
          return '검색 조건이 허용된 길이 조건에 맞지 않습니다.';
        case '12110':
          return '검색 조건에 특수문자는 사용할 수 없습니다.';
        default:
          return '';
      }
    }
  }

  // MANAGEMENT ACCOUNT INFO GET (DYNAMIC)

  if (endpoint === '/admin/management/account/{id}') {
    if (method === 'GET') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '선택한 관리자 조회가 실패했습니다.';
        case '12010':
          return '조회할 항목을 선택하세요.';
        default:
          return '';
      }
    }
  }

  // MANAGEMENT ACCOUNT INFO POST

  if (endpoint === '/admin/management/account') {
    if (method === 'PUT') {
      switch (code) {
        case '1':
          return '수정 되었습니다.';
        case '0':
          return '수정이 실패했습니다.';
        case '30010':
          return '로그인한 관리자의 정보가 없습니다.';
        case '12010':
          return '빈값이 허용되지 않는 전송 항목이 있습니다.';
        case '12110':
          return '허용되지 않은 형식의 값이 전송 항목에 있습니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 전송 항목이 있습니다.';
        default:
          return '';
      }
    }
  }

  // ------------------------------------------------------------------------

  // STAFF

  // ONETIME DOWNLOAD GET

  // if (endpoint === '/user/download/onetime/{id}') {
  if (endpoint === '/user/download/onetime/{id}') {
    if (method === 'GET') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '파일정보가 없어 다운로드 불가합니다.';
        case '53010':
          return '해당 파일은 만료일자가 지나서 다운로드 할 수 없습니다.';
        case '53020':
          return '해당 파일은 다운로드가 허용되지 않습니다.';
        case '53100':
          // return '해당 파일은 비밀번호가 필요합니다. 비밀번호 입력화면으로 이동합니다.';
          return '';
        default:
          return '';
      }
    }
  }

  // ONETIME DOWNLOAD POST (DYNAMIC)

  // if (endpoint === '/user/download/onetime/{id}') {
  if (endpoint === '/user/download/onetime/{id}') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '파일 비밀번호가 틀립니다. 다시 입력해 주세요.';
        case '12010':
          return '파일 비밀번호 입력해 주세요';
        case '12301':
          return '9 ~ 20자까지 입력할 수 있습니다.';
        case '12120':
          return '영어 대문자, 영어소문자, 숫자, 특수문자 각 1개 이상 조합해야 합니다.';
        default:
          return '';
      }
    }
  }

  // SHARE DOWNLOAD GET

  // if (endpoint === '/user/download/share/{id}') {
  if (endpoint === '/user/download/share') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '파일정보가 없어 다운로드 불가합니다.';
        default:
          return '';
      }
    }
  }

  // ------------------------------------------------------------------------

  // STAFF

  // SIGNIN

  if (endpoint === '/staff/jwt/signin') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '직원 인증이 실패했습니다.';
        case '33000':
          return '마크애니 직원 가입이 필요합니다. 가입 화면으로 이동합니다.';
        case '33200':
          return '마크애니 Email 도메인이 아닙니다.';
        case '33180':
          return '일시정지 중인 직원입니다.';
        case '33190':
          return '퇴사한 직원입니다.';
        case '33199':
          return '직원 상태가 정상이 아닙니다.';
        case '33210':
          return 'Google Oauth 토큰 검증이 실패했습니다.';
        case '33220':
          return 'Google 유저 조회 결과가 없습니다.';
        default:
          return '';
      }
    }
  }

  // AUTH

  if (endpoint === '/staff/jwt/auth') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '인증 토큰 인가가 실패했습니다.';
        case '15200':
          return '인증 토큰 유효성 검증이 실패했습니다. 인증 절차를 다시 진행하십시오.';
        case '15210':
          return '인증 토큰이 만료되었습니다. 재발급을 진행하십시오.';
        case '15230':
          return 'ROLE 검증이 실패했습니다.';
        default:
          return '';
      }
    }
  }

  // REISSUE

  if (endpoint === '/staff/jwt/reissue') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '직원 인가 검증이 실패했습니다.';
        case '15300':
          return 'Refresh Token 유효성 검증이 실패했습니다. 인증 절차를 다시 진행하십시오.';
        case '15310':
          return 'Refresh Token 유효시간이 만료되었습니다.';
        case '15230':
          return 'ROLE 검증이 실패했습니다.';
        default:
          return '';
      }
    }
  }

  // STAFF JOIN POST

  if (endpoint === '/staff/join') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '가입 정보가 없습니다.';
        case '12010':
          return '빈값이 허용되지 않는 수정 항목이 있습니다.';
        default:
          return '';
      }
    }
  }

  // PAGE INFO GET

  if (endpoint === '/staff/mypage/info') {
    if (method === 'GET') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '조회가 실패했습니다.';
        case '33010':
          return '로그인한 직원의 정보가 없습니다.';
        default:
          return '';
      }
    }
  }

  // ONETIME LIST GET
  if (endpoint === '/staff/lfs/onetime') {
    if (method === 'GET') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '고객 전달용 대용량 파일 목록 조회가 실패했습니다.';
        case '12301':
          return '검색 조건이 허용된 길이 조건에 맞지 않습니다.';
        case '12110':
          return '검색 조건에 특수문자는 사용할 수 없습니다.';
        default:
          return '';
      }
    }
  }

  // ONETIME INFO GET (DYNAMIC)
  if (endpoint === '/staff/lfs/onetime/{id}') {
    if (method === 'GET') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return '선택한 고객 전달용 대용량 파일 정보가 없습니다.';
        case '53010':
          return '만료일자가 지난 정보는 조회할 수 없습니다.';
        case '12010':
          return '조회할 항목을 선택하세요.';
        default:
          return '';
      }
    }
  }

  // ONETIME INFO POST
  if (endpoint === '/staff/lfs/onetime') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          // return '등록 되었습니다.';
          return '';
        case '0':
          return '등록이 실패했습니다.';
        case '30010':
          return '로그인한 관리자의 정보가 없습니다.';
        case '12010':
          return '빈값이 허용되지 않는 입력 항목이 있습니다.';
        case '12110':
          return '허용되지 않은 형식의 값이 입력 항목에 있습니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 입력 항목이 있습니다.';
        default:
          return '';
      }
    }
  }

  // ONETIME INFO PUT
  if (endpoint === '/staff/lfs/onetime') {
    if (method === 'PUT') {
      switch (code) {
        case '1':
          return '수정 되었습니다.';
        case '0':
          return '수정이 실패했습니다.';
        case '30010':
          return '로그인한 관리자의 정보가 없습니다.';
        case '53010':
          return '만료일자가 지난 정보는 수정할 수 없습니다.';
        case '12010':
          return '빈값이 허용되지 않는 수정 항목이 있습니다.';
        case '12110':
          return '허용되지 않은 형식의 값이 수정 항목에 있습니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 수정 항목이 있습니다.';
        default:
          return '';
      }
    }
  }

  // ONETIME INFO DELETE
  if (endpoint === '/staff/lfs/onetime') {
    if (method === 'DELETE') {
      switch (code) {
        case '1':
          return '삭제 되었습니다.';
        case '0':
          return '삭제가 실패했습니다.';
        case '12010':
          return '삭제할 항목을 선택하세요.';
        default:
          return '';
      }
    }
  }

  // ONETIME S3 UPLOAD ID POST

  if (endpoint === '/staff/lfs/onetime/s3/upload/id') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return 'S3업로드ID 생성이 실패했습니다.';
        case '53110':
          return '더 이상 업로드 할 수 없습니다.';
        case '53120':
          return '만료일이 지나서 업로드할 수 없습니다.';
        case '12010':
          return '빈값이 허용되지 않는 전송 항목이 있습니다.';
        case '12110':
          return '허용되지 않은 형식의 값이 전송 항목에 있습니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 전송 항목이 있습니다.';
        default:
          return '';
      }
    }
  }

  // ONETIME S3 PRESIGNED URL POST

  if (endpoint === '/staff/lfs/onetime/s3/part/presigned/url') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return 'Presigned-Url 요청이 실패했습니다.';
        case '12010':
          return '빈값이 허용되지 않는 전송 항목이 있습니다.';
        case '12110':
          return '허용되지 않은 형식의 값이 전송 항목에 있습니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 전송 항목이 있습니다.';
        default:
          return '';
      }
    }
  }

  // ONETIME S3 PART ETAG INSERT POST

  if (endpoint === '/staff/lfs/onetime/s3/part/etag/insert') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '';
        case '0':
          return 'E-Tag 정보 업데이트가 실패했습니다.';
        case '12010':
          return '빈값이 허용되지 않는 전송 항목이 있습니다.';
        case '12110':
          return '허용되지 않은 형식의 값이 전송 항목에 있습니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 전송 항목이 있습니다.';
        default:
          return '';
      }
    }
  }

  // ONETIME S3 UPLOAD STOP POST

  if (endpoint === '/staff/lfs/onetime/s3/upload/stop') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '업로드가 중지되었습니다.';
        case '0':
          return '업로드 중지가 실패했습니다.';
        case '12010':
          return '빈값이 허용되지 않는 전송 항목이 있습니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 전송 항목이 있습니다.';
        default:
          return '';
      }
    }
  }

  // ONETIME S3 REPORT DONE POST

  if (endpoint === '/staff/lfs/onetime/s3/report/done') {
    if (method === 'POST') {
      switch (code) {
        case '1':
          return '등록 되었습니다.';
        case '0':
          return '등록이 실패했습니다.';
        case '12010':
          return '빈값이 허용되지 않는 전송 항목이 있습니다.';
        case '12110':
          return '허용되지 않은 형식의 값이 전송 항목에 있습니다.';
        case '12301':
          return '허용된 길이 조건에 맞지 않는 전송 항목이 있습니다.';
        case '14100':
          return '멀티파트 E-Tag 리스트 값이 정의된 형식이 아닙니다.';
        case '14110':
          return '멀티파트 E-Tag 리스트 값 검증이 실패했습니다.';
        default:
          return '';
      }
    }
  }

  return '';
};
